import { useState } from 'react';
import { track } from '@vercel/analytics';
import { usePDIContext } from '../context/PDIContext';
import { CHECKLIST_SECTIONS, getAllItems } from '../data/checklistData';
import { DELIVERY_BLOCKERS } from './DeliveryBlockerBanner';
import {
  createPdf,
  PDF_COLORS as colors,
  SITE_URL,
  statusColor,
  statusBg,
  statusText,
} from '../utils/pdfCore';
import {
  FileDown,
  Loader2,
  X,
  Share2,
  Image,
  FileText,
} from 'lucide-react';

const PDFExport = ({ onClose }) => {
  const {
    itemStatuses,
    notes,
    pdiScore,
    carProfile,
    sectionProgress,
    photos,
    showToast,
  } = usePDIContext();

  const [isExporting, setIsExporting] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [exportProgress, setExportProgress] = useState('');

  const selectedFeatures = carProfile?.features || [];

  // Get critical issues
  const getCriticalIssues = () => {
    const allItems = getAllItems(selectedFeatures);
    return allItems.filter(item =>
      itemStatuses[item.id] === 'fail' ||
      (DELIVERY_BLOCKERS.includes(item.id) && itemStatuses[item.id] === 'fail')
    ).map(item => ({
      ...item,
      status: itemStatuses[item.id],
      note: notes[item.id] || '',
      photos: photos[item.id] || [],
    }));
  };

  // Shared PDF generation function
  const generateFullPDF = async (onProgress) => {
    onProgress('Preparing...');

    const pdf = await createPdf({ margin: 12 });
    const { doc, pageWidth, margin, contentWidth } = pdf;

    onProgress('Creating cover page...');

    // ========== COVER PAGE ==========
    pdf.rect(0, 0, pageWidth, 70, 0, colors.dark);

    doc.setFontSize(32);
    doc.setTextColor(255, 255, 255);
    doc.text('Car PDI Report', pageWidth / 2, 30, { align: 'center' });

    doc.setFontSize(12);
    doc.setTextColor(180, 180, 180);
    doc.text('Pre-Delivery Inspection Summary', pageWidth / 2, 42, { align: 'center' });

    doc.setFontSize(10);
    doc.setTextColor(140, 140, 140);
    doc.text(new Date().toLocaleDateString('en-IN', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }), pageWidth / 2, 52, { align: 'center' });

    doc.setTextColor(...colors.primary);
    doc.text(SITE_URL, pageWidth / 2, 62, { align: 'center' });

    pdf.y = 80;

    // Score Card (large, prominent)
    const scoreColor = pdiScore.scorePercentage >= 90 ? colors.pass :
                       pdiScore.scorePercentage >= 70 ? colors.warning : colors.danger;

    pdf.rect(margin, pdf.y, contentWidth, 40, 4, colors.veryLight);

    // Score badge
    pdf.rect(margin + 10, pdf.y + 5, 40, 30, 4, scoreColor);
    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255);
    doc.text(`${pdiScore.scorePercentage}%`, margin + 30, pdf.y + 24, { align: 'center' });

    // Score label
    doc.setFontSize(14);
    doc.setTextColor(...colors.dark);
    doc.text('PDI Score', margin + 55, pdf.y + 15);
    doc.setFontSize(10);
    doc.setTextColor(...colors.medium);
    doc.text(`Based on ${pdiScore.checked} items checked out of ${pdiScore.total}`, margin + 55, pdf.y + 23);

    // Quick stats on the right
    doc.setFontSize(9);
    const statsX = pageWidth - margin - 60;
    doc.setTextColor(...colors.pass);
    doc.text(`[OK] ${pdiScore.pass} Passed`, statsX, pdf.y + 12);
    doc.setTextColor(...colors.danger);
    doc.text(`[X] ${pdiScore.fail} Failed`, statsX, pdf.y + 20);
    doc.setTextColor(...colors.warning);
    doc.text(`[!] ${pdiScore.attention} Attention`, statsX, pdf.y + 28);
    doc.setTextColor(...colors.light);
    doc.text(`[ ] ${pdiScore.pending} Pending`, statsX, pdf.y + 36);

    pdf.y += 50;

    // Vehicle Information
    if (carProfile && (carProfile.carMake || carProfile.carModel || carProfile.vin)) {
      pdf.rect(margin, pdf.y, contentWidth, 35, 3, colors.white);
      pdf.strokeRect(margin, pdf.y, contentWidth, 35, 3, colors.veryLight, 1);

      doc.setFontSize(11);
      doc.setTextColor(...colors.dark);
      doc.text('Vehicle Information', margin + 5, pdf.y + 8);

      doc.setFontSize(9);
      doc.setTextColor(...colors.medium);

      let infoY = pdf.y + 15;
      if (carProfile.carMake || carProfile.carModel) {
        doc.text(`Vehicle: ${[carProfile.carMake, carProfile.carModel, carProfile.variant].filter(Boolean).join(' ')}`, margin + 5, infoY);
        infoY += 5;
      }
      if (carProfile.color) {
        doc.text(`Color: ${carProfile.color}`, margin + 5, infoY);
        infoY += 5;
      }
      if (carProfile.vin) {
        doc.text(`VIN: ${carProfile.vin}`, margin + 5, infoY);
      }
      if (carProfile.dealerName) {
        doc.text(`Dealer: ${carProfile.dealerName}`, margin + contentWidth / 2, pdf.y + 15);
      }
      if (carProfile.invoiceDate) {
        doc.text(`Invoice Date: ${carProfile.invoiceDate}`, margin + contentWidth / 2, pdf.y + 20);
      }

      pdf.y += 42;
    }

    // Section Progress Overview
    doc.setFontSize(11);
    doc.setTextColor(...colors.dark);
    doc.text('Section Progress', margin, pdf.y);
    pdf.y += 6;

    pdf.rect(margin, pdf.y, contentWidth, 55, 3, colors.veryLight);

    const sectionsPerRow = 3;
    const cellWidth = (contentWidth - 10) / sectionsPerRow;
    const cellHeight = 16;

    CHECKLIST_SECTIONS.forEach((section, i) => {
      const row = Math.floor(i / sectionsPerRow);
      const col = i % sectionsPerRow;
      const cellX = margin + 5 + (col * cellWidth);
      const cellY = pdf.y + 5 + (row * cellHeight);

      const progress = sectionProgress[section.id];
      const isComplete = progress.percentage === 100;

      doc.setFontSize(7);
      doc.setTextColor(...colors.dark);
      const shortTitle = section.title.length > 18 ? section.title.substring(0, 18) + '...' : section.title;
      doc.text(shortTitle, cellX, cellY + 4);

      // Progress bar
      const barWidth = cellWidth - 20;
      pdf.rect(cellX, cellY + 6, barWidth, 3, 1, [226, 232, 240]);
      if (progress.percentage > 0) {
        const fillColor = isComplete ? colors.pass : colors.primary;
        const fillWidth = Math.max(2, (barWidth * progress.percentage) / 100);
        pdf.rect(cellX, cellY + 6, fillWidth, 3, 1, fillColor);
      }

      doc.setFontSize(6);
      const textCol = isComplete ? colors.pass : colors.medium;
      doc.setTextColor(textCol[0], textCol[1], textCol[2]);
      doc.text(`${progress.percentage}%`, cellX + barWidth + 2, cellY + 8);
    });

    pdf.y += 62;

    // ========== CRITICAL ISSUES PAGE ==========
    const criticalIssues = getCriticalIssues();

    if (criticalIssues.length > 0) {
      pdf.addPage();

      onProgress('Processing critical issues...');

      // Header
      pdf.rect(margin, pdf.y, contentWidth, 16, 3, colors.dangerLight);
      doc.setFillColor(...colors.danger);
      doc.rect(margin, pdf.y, 4, 16, 'F');

      doc.setFontSize(14);
      doc.setTextColor(...colors.danger);
      doc.text(`Critical Issues (${criticalIssues.length})`, margin + 10, pdf.y + 11);
      doc.setFontSize(8);
      doc.text('DO NOT ACCEPT DELIVERY until resolved', pageWidth - margin - 5, pdf.y + 11, { align: 'right' });

      pdf.y += 22;

      for (let i = 0; i < criticalIssues.length; i++) {
        const item = criticalIssues[i];
        onProgress(`Processing issue ${i + 1}/${criticalIssues.length}...`);

        // Calculate space needed
        let itemHeight = 20;
        if (item.note) itemHeight += 12;
        if (item.photos.length > 0) itemHeight += 35;

        pdf.newPageIfNeeded(itemHeight + 10);

        // Issue card
        pdf.rect(margin, pdf.y, contentWidth, itemHeight, 2, colors.white);
        doc.setDrawColor(...colors.danger);
        doc.setLineWidth(0.5);
        doc.line(margin, pdf.y + 2, margin, pdf.y + itemHeight - 2);

        // Number badge
        pdf.rect(margin + 4, pdf.y + 4, 8, 8, 2, colors.danger);
        doc.setFontSize(7);
        doc.setTextColor(255, 255, 255);
        doc.text(String(i + 1), margin + 8, pdf.y + 10, { align: 'center' });

        // Title and section
        doc.setFontSize(10);
        doc.setTextColor(...colors.dark);
        doc.text(item.title, margin + 16, pdf.y + 9);
        doc.setFontSize(7);
        doc.setTextColor(...colors.medium);
        doc.text(item.sectionTitle, margin + 16, pdf.y + 15);

        // Status badge
        pdf.rect(pageWidth - margin - 20, pdf.y + 4, 16, 6, 1, colors.danger);
        doc.setFontSize(6);
        doc.setTextColor(255, 255, 255);
        doc.text('FAILED', pageWidth - margin - 12, pdf.y + 8, { align: 'center' });

        let innerY = pdf.y + 20;

        // Note
        if (item.note) {
          pdf.rect(margin + 5, innerY, contentWidth - 10, 10, 1, colors.veryLight);
          doc.setFontSize(7);
          doc.setTextColor(...colors.medium);
          doc.text('Note:', margin + 8, innerY + 4);
          doc.setTextColor(...colors.dark);
          const noteText = item.note.length > 80 ? item.note.substring(0, 80) + '...' : item.note;
          doc.text(noteText, margin + 20, innerY + 4);
          innerY += 12;
        }

        // Photos
        if (item.photos.length > 0) {
          doc.setFontSize(7);
          doc.setTextColor(...colors.medium);
          doc.text(`Evidence Photos (${item.photos.length}):`, margin + 5, innerY + 3);
          innerY += 5;

          const photoSize = 28;
          for (let p = 0; p < Math.min(item.photos.length, 5); p++) {
            const photoX = margin + 5 + (p * (photoSize + 3));
            pdf.photo(item.photos[p].data, photoX, innerY, photoSize);
          }

          if (item.photos.length > 5) {
            doc.setFontSize(7);
            doc.setTextColor(...colors.medium);
            doc.text(`+${item.photos.length - 5} more`, margin + 5 + (5 * (photoSize + 3)), innerY + 14);
          }
        }

        pdf.y += itemHeight + 5;
      }
    }

    // ========== DETAILED INSPECTION BY SECTION ==========
    onProgress('Creating detailed report...');

    pdf.addPage();

    // Header
    pdf.rect(margin, pdf.y, contentWidth, 14, 3, colors.dark);
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text('Detailed Inspection Results', margin + 5, pdf.y + 10);
    pdf.y += 20;

    // Process each section
    for (let sectionIdx = 0; sectionIdx < CHECKLIST_SECTIONS.length; sectionIdx++) {
      const section = CHECKLIST_SECTIONS[sectionIdx];
      const progress = sectionProgress[section.id];

      onProgress(`Processing ${section.title}...`);

      pdf.newPageIfNeeded(30);

      // Section header
      const sectionColor = progress.percentage === 100 ? colors.primaryLight : colors.veryLight;
      pdf.rect(margin, pdf.y, contentWidth, 12, 2, sectionColor);

      doc.setFontSize(10);
      doc.setTextColor(...colors.dark);
      doc.text(`${sectionIdx + 1}. ${section.title}`, margin + 4, pdf.y + 8);

      // Progress indicator
      doc.setFontSize(8);
      const progressColor = progress.percentage === 100 ? colors.pass : colors.medium;
      doc.setTextColor(...progressColor);
      doc.text(`${progress.completed}/${progress.total} (${progress.percentage}%)`, pageWidth - margin - 4, pdf.y + 8, { align: 'right' });

      pdf.y += 15;

      // Items with notes/photos, or failed/attention items
      const sectionItems = section.items.filter(item => {
        const status = itemStatuses[item.id];
        const hasNote = notes[item.id] && notes[item.id].trim() !== '';
        const hasPhotos = photos[item.id] && photos[item.id].length > 0;
        const isIssue = status === 'fail' || status === 'attention';
        return hasNote || hasPhotos || isIssue;
      });

      if (sectionItems.length === 0) {
        // Show compact summary if no items with details
        doc.setFontSize(8);
        doc.setTextColor(...colors.medium);
        if (progress.percentage === 100) {
          doc.text('All items passed - no issues found', margin + 4, pdf.y);
        } else if (progress.completed === 0) {
          doc.text('No items checked yet', margin + 4, pdf.y);
        } else {
          doc.text('Checked items passed - no notable issues', margin + 4, pdf.y);
        }
        pdf.y += 8;
      } else {
        for (const item of sectionItems) {
          const status = itemStatuses[item.id] || 'pending';
          const itemNote = notes[item.id] || '';
          const itemPhotos = photos[item.id] || [];

          // Calculate item height
          let itemHeight = 12;
          if (itemNote) itemHeight += 8;
          if (itemPhotos.length > 0) itemHeight += 25;

          pdf.newPageIfNeeded(itemHeight + 5);

          // Item row
          pdf.rect(margin + 2, pdf.y, contentWidth - 4, itemHeight, 1, statusBg(status));

          // Status indicator line
          doc.setFillColor(...statusColor(status));
          doc.rect(margin + 2, pdf.y + 1, 2, itemHeight - 2, 'F');

          // Item title
          doc.setFontSize(8);
          doc.setTextColor(...colors.dark);
          doc.text(item.title, margin + 8, pdf.y + 5);

          // Status badge
          pdf.rect(pageWidth - margin - 18, pdf.y + 1, 14, 5, 1, statusColor(status));
          doc.setFontSize(5);
          doc.setTextColor(255, 255, 255);
          doc.text(statusText(status), pageWidth - margin - 11, pdf.y + 4.5, { align: 'center' });

          let itemY = pdf.y + 8;

          // Note
          if (itemNote) {
            doc.setFontSize(6);
            doc.setTextColor(...colors.medium);
            const noteLines = doc.splitTextToSize(`Note: ${itemNote}`, contentWidth - 20);
            noteLines.slice(0, 2).forEach(line => {
              doc.text(line, margin + 8, itemY + 3);
              itemY += 3.5;
            });
          }

          // Photos (smaller thumbnails for section view)
          if (itemPhotos.length > 0) {
            const thumbSize = 18;
            for (let p = 0; p < Math.min(itemPhotos.length, 6); p++) {
              const photoX = margin + 8 + (p * (thumbSize + 2));
              pdf.photo(itemPhotos[p].data, photoX, itemY, thumbSize);
            }
            if (itemPhotos.length > 6) {
              doc.setFontSize(6);
              doc.setTextColor(...colors.medium);
              doc.text(`+${itemPhotos.length - 6}`, margin + 8 + (6 * (thumbSize + 2)), itemY + 10);
            }
          }

          pdf.y += itemHeight + 3;
        }
      }

      pdf.y += 5;
    }

    // ========== FINAL SUMMARY PAGE ==========
    pdf.addPage();

    onProgress('Creating summary...');

    // Header
    pdf.rect(margin, pdf.y, contentWidth, 14, 3, colors.dark);
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text('Inspection Summary & Recommendation', margin + 5, pdf.y + 10);
    pdf.y += 20;

    // Recommendation box
    let recommendation, recColor, recBg;
    if (pdiScore.fail > 0 || criticalIssues.length > 0) {
      recommendation = 'DO NOT ACCEPT DELIVERY - Critical issues must be resolved';
      recColor = colors.danger;
      recBg = colors.dangerLight;
    } else if (pdiScore.attention > 0) {
      recommendation = 'PROCEED WITH CAUTION - Minor issues noted, get written acknowledgment';
      recColor = colors.warning;
      recBg = colors.warningLight;
    } else if (pdiScore.pending > pdiScore.total * 0.2) {
      recommendation = 'INCOMPLETE INSPECTION - Please check remaining items before deciding';
      recColor = colors.medium;
      recBg = colors.veryLight;
    } else {
      recommendation = 'READY FOR DELIVERY - Vehicle passed inspection';
      recColor = colors.pass;
      recBg = colors.primaryLight;
    }

    pdf.rect(margin, pdf.y, contentWidth, 20, 3, recBg);
    doc.setFillColor(...recColor);
    doc.rect(margin, pdf.y + 2, 4, 16, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...recColor);
    doc.text(recommendation, margin + 10, pdf.y + 12);
    pdf.y += 28;

    // Stats summary
    pdf.rect(margin, pdf.y, contentWidth, 30, 3, colors.veryLight);

    const statWidth = contentWidth / 4;
    const statItems = [
      { label: 'Total Items', value: pdiScore.total, color: colors.dark },
      { label: 'Passed', value: pdiScore.pass, color: colors.pass },
      { label: 'Failed', value: pdiScore.fail, color: colors.danger },
      { label: 'Attention', value: pdiScore.attention, color: colors.warning },
    ];

    statItems.forEach((stat, i) => {
      const statX = margin + (i * statWidth) + statWidth / 2;
      doc.setFontSize(18);
      doc.setTextColor(...stat.color);
      doc.text(String(stat.value), statX, pdf.y + 14, { align: 'center' });
      doc.setFontSize(7);
      doc.setTextColor(...colors.medium);
      doc.text(stat.label, statX, pdf.y + 22, { align: 'center' });
    });

    pdf.y += 38;

    // Next steps
    doc.setFontSize(10);
    doc.setTextColor(...colors.dark);
    doc.text('Next Steps:', margin, pdf.y);
    pdf.y += 6;

    const steps = criticalIssues.length > 0 ? [
      '1. Share this report with the dealer',
      '2. Get written acknowledgment for all issues',
      '3. Request fixes before accepting delivery',
      '4. Re-inspect after repairs are made',
      '5. Do not sign delivery documents until satisfied',
    ] : [
      '1. Keep this report for your records',
      '2. Collect all documents (invoice, warranty, insurance)',
      '3. Get both key sets and verify they work',
      '4. Complete the delivery formalities',
      '5. Enjoy your new car!',
    ];

    pdf.rect(margin, pdf.y, contentWidth, steps.length * 7 + 6, 2, colors.veryLight);
    doc.setFontSize(8);
    doc.setTextColor(...colors.medium);
    steps.forEach((step, i) => {
      doc.text(step, margin + 5, pdf.y + 6 + (i * 7));
    });

    pdf.y += steps.length * 7 + 15;

    // Signature section
    pdf.rect(margin, pdf.y, contentWidth, 30, 2, colors.white);
    pdf.strokeRect(margin, pdf.y, contentWidth, 30, 2, colors.light);

    doc.setFontSize(8);
    doc.setTextColor(...colors.dark);
    doc.text('Customer Signature: _______________________', margin + 5, pdf.y + 10);
    doc.text('Date: _______________', margin + 5, pdf.y + 18);
    doc.text('Dealer Acknowledgment: _______________________', margin + contentWidth / 2, pdf.y + 10);
    doc.text('Date: _______________', margin + contentWidth / 2, pdf.y + 18);

    pdf.footer();

    onProgress('Finalizing...');
    return doc;
  };

  // Handle PDF download
  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const doc = await generateFullPDF(setExportProgress);
      const fileName = `PDI_Report_${carProfile?.carModel || 'Car'}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      track('downloaded_report');
      showToast('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF export error:', error);
      showToast('Failed to export PDF');
    } finally {
      setIsExporting(false);
      setExportProgress('');
    }
  };

  // Handle share via Web Share API
  const handleShare = async () => {
    setIsSharing(true);
    try {
      const doc = await generateFullPDF(setExportProgress);
      const pdfBlob = doc.output('blob');
      const fileName = `PDI_Report_${carProfile?.carModel || 'Car'}_${new Date().toISOString().split('T')[0]}.pdf`;
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Car PDI Report',
          text: `PDI Report for ${carProfile?.carMake || ''} ${carProfile?.carModel || 'Car'} - Score: ${pdiScore.scorePercentage}%`,
        });
        track('shared_report');
        showToast('Shared successfully');
      } else {
        // Fallback: download the file
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
        track('downloaded_report');
        showToast('PDF downloaded (sharing not supported on this device)');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
        showToast('Failed to share');
      }
    } finally {
      setIsSharing(false);
      setExportProgress('');
    }
  };

  // Count items with photos and notes
  const photoCount = Object.values(photos).reduce((sum, arr) => sum + (arr?.length || 0), 0);
  const noteCount = Object.values(notes).filter(n => n && n.trim() !== '').length;

  return (
    <div className="modal-backdrop flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="px-5 py-4 bg-gradient-to-r from-slate-800 to-slate-900 text-white flex items-center justify-between">
          <div>
            <h2 className="font-display font-semibold text-lg">Export Report</h2>
            <p className="text-xs text-slate-300 mt-0.5">Share or download your PDI results</p>
          </div>
          <button onClick={onClose} className="p-2 -m-2 text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stats Preview */}
        <div className="px-5 pt-4 pb-2">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5 text-slate-600">
              <Image className="w-4 h-4 text-brand-500" />
              <span>{photoCount} photos</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-600">
              <FileText className="w-4 h-4 text-amber-500" />
              <span>{noteCount} notes</span>
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="p-5 pt-2 space-y-3">
          {/* PDF Export */}
          <button
            onClick={handleExportPDF}
            disabled={isExporting || isSharing}
            className="w-full p-4 bg-gradient-to-br from-brand-50 to-teal-50 border-2 border-brand-200 rounded-xl flex items-center gap-4 hover:border-brand-300 hover:shadow-lg transition-all btn-touch disabled:opacity-70"
          >
            {isExporting ? (
              <div className="w-12 h-12 rounded-xl bg-brand-100 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-brand-600 animate-spin" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-500 to-teal-500 flex items-center justify-center shadow-lg">
                <FileDown className="w-6 h-6 text-white" />
              </div>
            )}
            <div className="text-left flex-1">
              <p className="font-bold text-brand-800 text-lg">Download PDF</p>
              <p className="text-sm text-brand-600">
                {isExporting ? exportProgress : 'Complete report with photos & notes'}
              </p>
            </div>
          </button>

          {/* Share Button */}
          <button
            onClick={handleShare}
            disabled={isExporting || isSharing}
            className="w-full p-4 bg-gradient-to-br from-violet-50 to-purple-50 border-2 border-violet-200 rounded-xl flex items-center gap-4 hover:border-violet-300 hover:shadow-lg transition-all btn-touch disabled:opacity-70"
          >
            {isSharing ? (
              <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-violet-600 animate-spin" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center shadow-lg">
                <Share2 className="w-6 h-6 text-white" />
              </div>
            )}
            <div className="text-left flex-1">
              <p className="font-bold text-violet-800 text-lg">Share Report</p>
              <p className="text-sm text-violet-600">
                {isSharing ? exportProgress : 'Share PDF via WhatsApp, Email, etc.'}
              </p>
            </div>
          </button>
        </div>

        {/* Info */}
        <div className="px-5 pb-5">
          <p className="text-xs text-slate-500 text-center">
            The PDF includes all inspection details, notes, and photos
          </p>
        </div>
      </div>
    </div>
  );
};

export default PDFExport;
